<template>
  <div v-if="profile && profile.type <= 2">
    <page-header :header="`${type} Page`">
      <router-link
        v-if="id && currentPage && currentPage.menu && currentPage.published"
        :to="`/${currentPage.menu.slug}/${currentPage.slug}`"
        class="inline-block no-underline bg-white text-black rounded-lg p-4 uppercase text-sm font-medium align-bottom"
      >
        <i class="far fa-eye fa-fw fa-lg mr-1"></i>
        Preview
      </router-link>
      <button v-if="id" @click="showDeleteModal = true" class="bg-white rounded-lg p-4 uppercase text-sm font-medium align-bottom ml-4">
        <i class="far fa-trash-alt fa-fw fa-lg mr-1" />
        Delete Page
      </button>
      <deletion-modal
        v-if="showDeleteModal"
        @confirmed="destroy"
        @leave="showDeleteModal = false"
        @dismissed="showDeleteModal = false"
      >
        <p class="text-center">Are you sure you wish to delete the page? This cannot be undone.</p>
      </deletion-modal>
    </page-header>

    <div class="mb-6 -mt-2 pb-4 border-b-4">
      <div class="flex flex-wrap -mx-4">
        <div class="px-4 w-full lg:w-2/3">
          <p class="mb-1">Page title and intro text:</p>
          <input v-model="currentPage.title" type="text" name="title" id="title" placeholder="Title" class="input w-full mb-3 bg-white" :class="{ 'border-red-dark': this.errorBag.get('title') }">
          <input v-model="currentPage.intro" type="text" name="subtitle" id="subtitle" placeholder="Subtitle" class="input w-full mb-3 bg-white">
          <p class="mb-1">Page Slug:</p>
          <input v-model="currentPage.slug" type="text" name="slug" id="slug" placeholder="Slug" class="input w-full mb-3 bg-white" :class="{ 'border-red-dark': this.errorBag.get('slug') }">
          <div class="flex flex-wrap items-center" v-if="system.networks.length > 1">
              <p class="w-full mb-2">Page Visibility by Network:</p>
               <div class="xl:flex-row md:flex md:flex-row sm:flex-col space-between">
                <div v-for="(network, i) in system.networks" :key="i" class="text-sm uppercase">
                  <input  type="radio"  :id="+i" name="network.id" :value="network.id" v-model="currentPage.assigned_network">
                  <label  :for="+i" class="ml-4 mt-2">{{ network.name }}</label>
                </div>
                <input  type="radio" id="all" name="network.id"  :value="null" v-model="currentPage.assigned_network">
                <label  for="all"  class="ml-4 mt-2">ALL</label>
               </div>
            </div>
            <div v-if="country_list && [7, 8].includes(selectedOffice.network_id) && system.networks.length > 1">
              <div class="flex mt-6 -mb-4">
                <p class="w-1/2">Page visible to (Country):</p>
                <checkbox-input label="Select all" @input="toggleSelectAllCountries" :value="true" />
              </div>
              <select-input
                v-model="currentPage.assigned_countries"
                :options="country_list"
              />
              <div class="flex -mt-3" v-show="country_list.length>0">
                <div class="w-full text-right text-sm text-gray-600">
                  {{ currentPage.assigned_countries.length }} selected
                </div>
              </div>
            </div>
        </div>
        <div class="px-4 w-full lg:w-1/3">
          <div class="mb-1 flex justify-between items-baseline">
            <p>Menu location:</p>
            <button class="shadow-none" @click="isManagingMenus = true">
              <small>Manage Menus</small>
            </button>
          </div>
          <pl-select v-model="currentPage.menu_id" name="filter_menu" id="filter_menu" class="w-full mb-3" :select-class="['bg-white', { 'border-red-dark': this.errorBag.get('menu_id') }]">
            <option :value="null" disabled>Please Select</option>
            <template v-for="menu in menus">
              <option :key="menu.id" :value="menu.id">{{ menu.name }}</option>
              <option
                v-for="childMenu in menu.children"
                :key="childMenu.id"
                :value="childMenu.id"
              >↳ {{ childMenu.name }}</option>
            </template>
          </pl-select>

          <div class="flex flex-wrap items-center">
            <div class="text-start w-full py-5">
              <input id="published" class="mr-2 leading-tight" type="checkbox" v-model="currentPage.published">
              <label for="published">
                <span class="text-sm">
                  Publish
                </span>
              </label>
              <div v-if="system.external_data.plan === true" class="mt-2">
               <input id="guild-lite" class="mr-2 leading-tight" type="checkbox" v-model="currentPage.guild_lite">
                <label for="guild-lite">
                  <span class="text-sm">
                    Guild lite
                  </span>
                </label>
              </div>
            </div>
          </div>
          <div class="flex flex-wrap items-center">
            <button @click="savePage" :disabled="isSaving" class="bg-tertiary text-white rounded px-4 py-3 uppercase text-sm font-medium align-bottom ml-auto w-full">
              <i v-if="isSaving" class="fas fa-circle-notch fa-fw fa-lg fa-spin" />
              <div v-else>
                <i class="far fa-save fa-fw fa-lg" />
                Save Page
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="mb-6 -mt-2 pb-4 border-b-4">
      <div class="flex -mx-4">
        <div class="px-4 w-full">
          <p class="mb-1">Product Module:</p>
          <pl-select v-model="currentPage.product_module" class="w-full my-2" :select-class="['bg-white', { 'border-red-dark': this.errorBag.get('product_module') }]">
            <option :value="null">None</option>
            <option v-for="(product_module, index) in system.product_modules_enabled" :key="index" :value="product_module.value">
              {{ product_module.name }}
            </option>
          </pl-select>
        </div>
      </div>
    </div>

    <div v-if="currentPage.type === 'content' && system.external_data.plan === true" class="mb-6 -mt-2 pb-4 border-b-4">
      <div class="flex -mx-4">
        <div class="px-4 w-full mb-4">
          <h3 class="mb-2">Available to guild plan</h3>
          <div class="flex justify-center">
            <div class="border-r-2 flex flex-col w-full items-center">

              <p class="font-semibold">Guild-Lite</p>
              <div>
                <div class="py-2">
                  <input id="Guildlight-included" class="mr-2 leading-tight" type="radio" value="Included" v-model="currentPage.available_plan['Lite']" @click="uncheck(currentPage.available_plan['Lite'], 'Lite')">
                  <label for="Guildlight-included">
                    <span class="text-sm">
                      Included
                    </span>
                  </label>
                </div>
                <div class="py-2">
                  <input id="Guildlight-discounted" class="mr-2 leading-tight" type="radio" value="Discounted" v-model="currentPage.available_plan['Lite']" @click="uncheck(currentPage.available_plan['Lite'], 'Lite')">
                  <label for="Guildlight-discounted">
                    <span class="text-sm">
                      Discounted
                    </span>
                  </label>
                </div>
                <div class="py-2">
                  <input id="Guildlight-locked" class="mr-2 leading-tight" type="radio" value="Locked" v-model="currentPage.available_plan['Lite']" @click="uncheck(currentPage.available_plan['Lite'], 'Lite')">
                  <label for="Guildlight-locked">
                    <span class="text-sm">
                      Locked
                    </span>
                  </label>
                </div>
              </div>
            </div>
            <div class="border-r-2 flex flex-col w-full items-center">
              <p class="font-semibold">Legacy</p>
              <div>
                <div class="py-2">
                  <input id="Legacy-included" class="mr-2 leading-tight" type="radio" value="Included" v-model="currentPage.available_plan['Legacy']" @click="uncheck(currentPage.available_plan['Legacy'], 'Legacy')">
                  <label for="Legacy-included">
                    <span class="text-sm">
                      Included
                    </span>
                  </label>
                </div>
                <div class="py-2">
                  <input id="Legacy-discounted" class="mr-2 leading-tight" type="radio" value="Discounted" v-model="currentPage.available_plan['Legacy']" @click="uncheck(currentPage.available_plan['Legacy'], 'Legacy')">
                  <label for="Legacy-discounted">
                    <span class="text-sm">
                      Discounted
                    </span>
                  </label>
                </div>
                <div class="py-2">
                  <input id="Legacy-locked" class="mr-2 leading-tight" type="radio" value="Locked" v-model="currentPage.available_plan['Legacy']" @click="uncheck(currentPage.available_plan['Legacy'], 'Legacy')">
                  <label for="Legacy-locked">
                    <span class="text-sm">
                      Locked
                    </span>
                  </label>
                </div>
              </div>
            </div>
            <div class="border-r-2 flex flex-col w-full items-center">
              <p class="font-semibold">Standard</p>
              <div>
                <div class="py-2">
                  <input id="Standard-included" class="mr-2 leading-tight" type="radio" value="Included" v-model="currentPage.available_plan['Standard']" @click="uncheck(currentPage.available_plan['Standard'], 'Standard')">
                  <label for="Standard-included">
                    <span class="text-sm">
                      Included
                    </span>
                  </label>
                </div>
                <div class="py-2">
                  <input id="Standard-discounted" class="mr-2 leading-tight" type="radio" value="Discounted" v-model="currentPage.available_plan['Standard']" @click="uncheck(currentPage.available_plan['Standard'], 'Standard')">
                  <label for="Standard-discounted">
                    <span class="text-sm">
                      Discounted
                    </span>
                  </label>
                </div>
                <div class="py-2">
                  <input id="Standard-locked" class="mr-2 leading-tight" type="radio" value="Locked" v-model="currentPage.available_plan['Standard']" @click="uncheck(currentPage.available_plan['Standard'], 'Standard')">
                  <label for="Standard-locked">
                    <span class="text-sm">
                      Locked
                    </span>
                  </label>
                </div>
              </div>
            </div>
            <div class="border-r-2 flex flex-col w-full items-center">
              <p class="font-semibold">Enhanced</p>
              <div>
                <div class="py-2">
                  <input id="Enhanced-included" class="mr-2 leading-tight" type="radio" value="Included" v-model="currentPage.available_plan['Enhanced']" @click="uncheck(currentPage.available_plan['Enhanced'], 'Enhanced')">
                  <label for="Enhanced-included">
                    <span class="text-sm">
                      Included
                    </span>
                  </label>
                </div>
                <div class="py-2">
                  <input id="Enhanced-discounted" class="mr-2 leading-tight" type="radio" value="Discounted" v-model="currentPage.available_plan['Enhanced']" @click="uncheck(currentPage.available_plan['Enhanced'], 'Enhanced')">
                  <label for="Enhanced-discounted">
                    <span class="text-sm">
                      Discounted
                    </span>
                  </label>
                </div>
                <div class="py-2">
                  <input id="Enhanced-locked" class="mr-2 leading-tight" type="radio" value="Locked" v-model="currentPage.available_plan['Enhanced']" @click="uncheck(currentPage.available_plan['Enhanced'], 'Enhanced')">
                  <label for="Enhanced-locked">
                    <span class="text-sm">
                      Locked
                    </span>
                  </label>
                </div>
              </div>
            </div>
            <div class="flex flex-col w-full items-center">
              <p class="font-semibold">Premium</p>
              <div>
                <div class="py-2">
                  <input id="Premium-included" class="mr-2 leading-tight" type="radio" value="Included" v-model="currentPage.available_plan['Premium']" @click="uncheck(currentPage.available_plan['Premium'], 'Premium')">
                  <label for="Premium-included">
                    <span class="text-sm">
                      Included
                    </span>
                  </label>
                </div>
                <div class="py-2">
                  <input id="Premium-discounted" class="mr-2 leading-tight" type="radio" value="Discounted" v-model="currentPage.available_plan['Premium']" @click="uncheck(currentPage.available_plan['Premium'], 'Premium')">
                  <label for="Premium-discounted">
                    <span class="text-sm">
                      Discounted
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <p class="font-semibold">Hide the first Module</p>
      <div class="ml-2 text-start w-full py-2 flex flex-col">
        <div>
          <pl-select name="filter_menu" id="filter_menu" v-model="currentPage.available_plan.hideFirstModule" class="bg-white w-1/4" :select-class="['bg-white']">
            <option value="never">Never</option>
            <option value="standard">For Standard and above</option>
            <option value="enhanced">For Enhanced and above</option>
            <option value="premium">For Premium</option>
          </pl-select>
        </div>
      </div>
      <p class="font-semibold">Hide Plan items</p>
      <div class="ml-2 text-start w-full py-2 flex flex-col">
        <div>
          <input id="hideicons" class="mr-2 leading-tight" type="checkbox" v-model="currentPage.available_plan.hidePlanIcons">
            <label for="hideicons">
              <span class="text-sm">
                Hide plan icons
              </span>
            </label>
        </div>
      </div>
    </div>

    <div class="mb-6 -mx-4 flex flex-wrap">
      <div class="w-full lg:w-2/3 px-4">
        <div class="bg-white rounded-lg p-6 pt-0 mt-12">
          <div class="flex flex-wrap items-center border-b mb-6 pb-2">
            <page-icon-picker
              :icon="currentPage.icon"
              :color="currentPage.icon_color"
              :icon_text_color="currentPage.icon_text_color"
              @change="iconSelected"
            />
            <input type="text" placeholder="Title" class="input w-2/3 mx-0 mr-4 sm:mx-6" v-model="blockTitle">
            <div class="rounded-full h-10 w-10 flex justify-center items-center border cursor-pointer" @click="informativeMediaIsShowing = true">
              <i class="fas fa-link fa-lg" />
            </div>
            <modal
              v-if="informativeMediaIsShowing"
              @leave="informativeMediaIsShowing = false"
              width="40vw"
              :scrollY="false"
              showCloseBtn="true"
            >
              <div class="flex items-center pb-4 border-b">
                <div class="w-16 h-16 flex justify-center items-center rounded-full border">
                  <i class="fas fa-link fa-2x" />
                </div>
                <span class="ml-4 text-2xl font-medium">Informative Media</span>
              </div>
              <div class="flex items-center py-6 border-b">
                <div class="w-1/4 mr-4 flex items-center">
                  <i class="fas fa-play fa-lg mx-4 text-tertiary" />
                  <span>Video Content</span>
                </div>
                <input type="url" class="input flex-grow" placeholder="https://" v-model="currentPage.video_url">
              </div>
              <!-- Disabled the gallery / documents, for now the media module can be used -->
              <!-- <div class="flex items-start py-6 border-b">
                <div class="w-1/4 mt-4 mr-4 flex items-center">
                  <i class="fas fa-image fa-lg mx-4 text-tertiary" />
                  <span>Gallery</span>
                </div>
                <div class="w-48 h-32 bg-grey-lighter rounded border border-grey border-dashed text-grey flex flex-col items-center justify-around py-4 cursor-pointer">
                  <i class="fas fa-image fa-2x" />
                  <span>Select Image</span>
                </div>
              </div> -->
              <!-- <div class="flex items-start py-6 border-b">
                <div class="w-1/4 mt-4 mr-4 flex items-center">
                  <i class="fas fa-file-download fa-lg mx-4 text-tertiary" />
                  <span>Download</span>
                </div>
                <div class="w-48 h-32 bg-grey-lighter rounded border border-grey border-dashed text-grey flex flex-col items-center justify-around py-4 cursor-pointer">
                  <i class="fas fa-file-upload fa-2x" />
                  <span>Select Document</span>
                </div>
              </div> -->
              <div class="flex items-center py-6 border-b">
                <div class="w-1/4 mr-4 flex items-center">
                  <i class="fas fa-book-open fa-lg mx-4 text-tertiary" />
                  <span>Page Turner</span>
                </div>
                <input type="url" class="input flex-grow" placeholder="https://" v-model="currentPage.page_turner_url">
              </div>
              <div class="flex items-center py-6">
                <div class="w-1/4 mr-4 flex items-center">
                  <i class="fas fa-laptop fa-lg mx-4 text-tertiary" />
                  <span>Website</span>
                </div>
                <input type="url" class="input flex-grow" placeholder="https://" v-model="currentPage.website_url">
              </div>
            </modal>
          </div>

          <div>
            <div v-for="(block, index) in blocks" :key="index"
              class="block relative" :class="{ 'mb-6 pb-4 border-b': index < blocks.length - 1 }">
              <div class="flex mb-2 items-center justify-between">
                <div class="flex" v-if="blocks.length > 1">
                  <button class="upButton bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded-l border flex items-center h-10 w-10 justify-center" :disabled="index==0" @click="move(index,index-1)"><i class="fas fa-chevron-up"></i></button>
                  <button class="downButton bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded-r border flex items-center h-10 w-10 justify-center" :disabled="index==(blocks.length-1)" @click="move(index,index+1)"><i class="fas fa-chevron-down"></i></button>
                </div>
                <button v-if="index > 0" type="button" @click="removeBlock(index)" class="bg-white rounded-full border h-6 w-6 flex justify-center items-center">
                  <i class="fas fa-times fa-xs" />
                </button>
              </div>
              <tinymce
                :init="editorConfig"
                v-model="block.settings.content"
                @onChange="onTinyMceContentChange"
              />
            </div>
            <div class="mt-4 pt-6 flex justify-center border-t">
              <button type="button" @click="addBlock" class="w-48 px-4 py-1 flex items-center justify-center text-grey-darkest rounded-lg shadow hover:shadow-none border border-blue bg-white">
                <span class="text-2xl mr-2">&#43;</span>
                Add Text Block
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="w-full lg:w-1/3 px-4 mt-12">
        <div v-for="(module, index) in modules" :key="index" class="relative">
          <div class="absolute pin-t pin-r bg-white -mt-2 -mr-2 rounded-full border h-6 flex text-xs overflow-hidden">
            <button type="button" @click="permissionsModule = module" class="w-6 h-6 border-r hover:bg-grey-lighter" title="Edit Visibility">
              <i class="fas" :class="module.permissions === null ? 'fa-unlock' : 'fa-lock'" />
            </button>
            <button type="button" class="w-6 h-6 hover:bg-grey-lighter" :disabled="index==0" @click="moveModule(index,index-1)">
              <i class="fas fa-chevron-up"></i>
            </button>
            <button type="button" class="w-6 h-6 hover:bg-grey-lighter" :disabled="index==(module.length-1)" @click="moveModule(index,index+1)">
              <i class="fas fa-chevron-down"></i>
            </button>
            <button type="button" @click="removeModule(index)" class="w-6 h-6 hover:bg-grey-lighter" title="Remove Module">
              <i class="fas fa-times" />
            </button>
          </div>

          <component :is="module.type" :module="module" class="mb-6"></component>
        </div>
        <module-picker v-if="isPickingModule" @click="addModule"></module-picker>
        <div class="flex justify-center">
          <button type="button" @click="isPickingModule = true" :disabled="isPickingModule"
            :class="{ 'cursor-not-allowed opacity-50': isPickingModule }"
            class="w-48 px-4 py-1 flex items-center justify-center text-grey-darkest rounded-lg shadow hover:shadow-none border border-blue bg-white">
            <span class="text-2xl mr-2">&#43;</span>
            Add Module
          </button>
        </div>
      </div>
    </div>

    <menu-manager v-if="isManagingMenus" @leave="isManagingMenus = false" />

    <div class="mb-6 -mt-2 pb-4 border-t-4">
      <div class="flex -mx-4 mt-4">
        <div class="px-4 w-full md:w-1/2">
          <panel class="w-full min-h-full flex flex-col" with-header>
            <div slot="header" class="flex items-center">
              <h3>Page Tags</h3>
            </div>
            <tag-input
              v-model="currentPage.tags"
              :error-bag="errorBag"
              error-key="tags"
              uppercase
            />
          </panel>
        </div>
      </div>
    </div>
    <modal
      v-if="permissionsModule"
      @leave="permissionsModule = null"
      :showCloseBtn="true"
    >
      <h3 class="bold mb-8">Module Visibility</h3>
      <div class="mb-6">
        <input type="checkbox" id="cb1" v-model="modulePermissionsEnabled">
        <label for="cb1">Display this module to all users.</label>
      </div>

      <div class="mb-6" v-if="!modulePermissionsEnabled">
        <label for="operator">Display this module when:</label>
        <div class="flex w-full -mx-2">
          <div class="w-full md:w-1/2 px-2">
            <select id="operator" v-model="permissionsModule.permissions.operator" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm">
              <option value="contains">Selected office has permission:</option>
              <option value="not_contains">Selected office does not have permission:</option>
            </select>
          </div>
          <div class="w-full md:w-1/2 px-2">
            <select v-model="permissionsModule.permissions.permission" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm">
              <option v-for="(label, value) in system.permissions" :value="value" :key="value">{{ label }}</option>
            </select>
          </div>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import MenuManager from '@/components/cms/MenuManager'
import ModulePicker from '@/components/cms/ModulePicker'
import CallToAction from '@/components/cms/CallToAction'
import GoldMarketingPack from '@/components/cms/GoldMarketingPack'
import SimpleCta from '@/components/cms/Link'
import Media from '@/components/cms/Media'
import LatestEzine from '@/components/LatestEzine'
import LatestPageTurner from '@/components/LatestPageTurner'
import ValuationMailer from '@/components/cms/ValuationMailer'
import DeletionModal from '@/components/Modals/DeletionModal'
import { head, find } from 'lodash'
import { mapState, mapMutations, mapActions } from 'vuex'
import api from '@/http'
import ErrorBag from '@/ErrorBag'
import PageIconPicker from '@/components/cms/PageIconPicker'
import mceConfig from '@/mixins/TinyMCE'
import DigitalHubLink from '@/components/cms/DigitalHubLink'
import PageTurners from '@/components/cms/PageTurners'
import Offerhive from '@/components/cms/Offerhive'
import Insight from '@/components/cms/Insight'
import FeeStreet from '@/components/cms/FeeStreet'
import QrCodeModule from '@/components/cms/QrCodeModule'
import CustomPrintModule from '@/components/cms/CustomPrintModule'
import Knowledgehub from '@/components/cms/Knowledgehub.vue'
import TicketEventBookingSetting from '@/components/cms/TicketEventBookingSetting'

export default {
  mixins: [ mceConfig ],
  components: { MenuManager,
    ModulePicker,
    CallToAction,
    GoldMarketingPack,
    Media,
    SimpleCta,
    PageIconPicker,
    DeletionModal,
    ValuationMailer,
    LatestEzine,
    LatestPageTurner,
    DigitalHubLink,
    PageTurners,
    Offerhive,
    Insight,
    FeeStreet,
    QrCodeModule,
    CustomPrintModule,
    Knowledgehub,
    'ticket-event-booking': TicketEventBookingSetting },
  props: ['id'],
  created () {
    console.log(this.id)
    this.loadPage()
    if (!this.id) {
      this.toggleSelectAllCountries(true)
    }
  },
  watch: {
    profile: {
      handler () {
        this.checkUser()
      },
      immediate: true
    },
    currentPage () {
      this.checkUser()
    },
    pages () {
      this.loadPage()
    },
    'currentPage.product_module': {
      handler () {
        if (!this.loaded) {
          return
        }

        if (this.currentPage.product_module === 'App\\Members\\ProductModules\\ReferralsModule') {
          this.currentPage.product_config = {
            terms: '',
            privacy: ''
          }
        }
      }
    },
    'currentPage.assigned_network': {
      handler () {
        if (!this.loaded) {
          return
        }
        this.country_list = [
          { 'id': 'GB', 'name': 'UK' },
          { 'id': 'UKA', 'name': 'UK Associate' },
          { 'id': 'AU', 'name': 'Australia' },
          { 'id': 'BB', 'name': 'Barbados' },
          { 'id': 'BG', 'name': 'Bulgaria' },
          { 'id': 'CY', 'name': 'Cyprus' },
          { 'id': 'EE', 'name': 'Estonia' },
          { 'id': 'FI', 'name': 'Finland' },
          { 'id': 'FR', 'name': 'France' },
          { 'id': 'DE', 'name': 'Germany' },
          { 'id': 'GR', 'name': 'Greece' },
          { 'id': 'HU', 'name': 'Hungary' },
          { 'id': 'IE', 'name': 'Ireland' },
          { 'id': 'IT', 'name': 'Italy' },
          { 'id': 'JE', 'name': 'Jersey' },
          { 'id': 'MU', 'name': 'Mauritius' },
          { 'id': 'NA', 'name': 'Namibia' },
          { 'id': 'PT', 'name': 'Portugal' },
          { 'id': 'ES', 'name': 'Spain' },
          { 'id': 'ZA', 'name': 'South Africa' },
          { 'id': 'CH', 'name': 'Switzerland' },
          { 'id': 'TH', 'name': 'Thailand' },
          { 'id': 'AE', 'name': 'UAE' },
          { 'id': 'ZW', 'name': 'Zimbabwe' }
        ]
        if (this.currentPage.assigned_network === 7) {
          this.country_list = this.country_list.splice(0, 2)
        } else if (this.currentPage.assigned_network === 8) {
          this.country_list = this.country_list.splice(2, this.country_list.length)
        }
        this.toggleSelectAllCountries(true)
      }
    }
  },
  data () {
    return {
      showDeleteModal: false,
      isSaving: false,
      isPickingModule: false,
      isManagingMenus: false,
      informativeMediaIsShowing: false,
      errorBag: new ErrorBag(),
      currentPage: {
        slug: null,
        assigned_network: null,
        menu_id: null,
        title: null,
        intro: null,
        type: 'content',
        published: false,
        guild_lite: false,
        available_plan: {},
        icon: null,
        menu: { slug: null },
        video_url: null,
        page_turner_url: null,
        website_url: null,
        content: [
          {
            type: 'text',
            title: '',
            settings: { content: '' },
            order: 0
          }
        ],
        modules: [],
        product_module: null,
        product_config: {},
        assigned_countries: []
      },
      country_list: [
        { 'id': 'GB', 'name': 'UK' },
        { 'id': 'UKA', 'name': 'UK Associate' },
        { 'id': 'AU', 'name': 'Australia' },
        { 'id': 'BB', 'name': 'Barbados' },
        { 'id': 'BG', 'name': 'Bulgaria' },
        { 'id': 'CY', 'name': 'Cyprus' },
        { 'id': 'EE', 'name': 'Estonia' },
        { 'id': 'FI', 'name': 'Finland' },
        { 'id': 'FR', 'name': 'France' },
        { 'id': 'DE', 'name': 'Germany' },
        { 'id': 'GR', 'name': 'Greece' },
        { 'id': 'HU', 'name': 'Hungary' },
        { 'id': 'IE', 'name': 'Ireland' },
        { 'id': 'IT', 'name': 'Italy' },
        { 'id': 'JE', 'name': 'Jersey' },
        { 'id': 'MU', 'name': 'Mauritius' },
        { 'id': 'NA', 'name': 'Namibia' },
        { 'id': 'PT', 'name': 'Portugal' },
        { 'id': 'ES', 'name': 'Spain' },
        { 'id': 'ZA', 'name': 'South Africa' },
        { 'id': 'CH', 'name': 'Switzerland' },
        { 'id': 'TH', 'name': 'Thailand' },
        { 'id': 'AE', 'name': 'UAE' },
        { 'id': 'ZW', 'name': 'Zimbabwe' }
      ],
      permissionsModule: null,
      loaded: false
    }
  },
  computed: {
    ...mapState([
      'pages', 'menus', 'system', 'selectedOffice', 'profile'
    ]),
    type () {
      return this.id ? 'Edit' : 'Create New'
    },
    blocks () {
      return this.currentPage.content
    },
    modules () {
      return this.currentPage.modules
    },
    blockTitle: {
      get () {
        if (this.blocks.length === 0) {
          this.addBlock()
        }

        return head(this.blocks).title
      },
      set (newValue) {
        head(this.blocks).title = newValue
      }
    },
    modulePermissionsEnabled: {
      get () {
        if (!this.permissionsModule) {
          return false
        }

        return this.permissionsModule.permissions === null
      },
      set (newValue) {
        if (newValue) {
          this.permissionsModule.permissions = null
          return
        }

        this.permissionsModule.permissions = {
          operator: 'contains',
          permission: Object.keys(this.system.permissions)[0]
        }
      }
    }
  },
  methods: {
    ...mapMutations([
      'setPage'
    ]),
    ...mapActions([
      'displayToastNotification'
    ]),
    head: head,
    uncheck (value, plan) {
      if (value === this.currentPage.available_plan[plan]) {
        this.currentPage.available_plan[plan] = null
      }
    },
    checkUser () {
      if (!this.profile || !this.currentPage.slug) {
        return
      }

      if (this.profile.type > 2) {
        this.$router.push(`/${this.currentPage.menu.slug}/${this.currentPage.slug}`)
      }
    },
    iconSelected (data) {
      this.currentPage.icon = data.icon
      this.currentPage.icon_color = data.color
      this.currentPage.icon_text_color = data.icon_text_color
    },
    loadPage () {
      if (this.id && this.pages && this.pages.length > 0) {
        let page = find(this.pages, p => p.slug === this.id)
        if (page) {
          this.currentPage = page
        }
      }

      setTimeout(() => {
        this.loaded = true
      }, 500)
    },
    handleSaveError (error) {
      this.errorBag.store(error.response.data)
      this.isSaving = false
      this.displayToastNotification({
        type: 'warning',
        icon: 'fa-bell',
        title: 'Unable to save page',
        info: error.response.data.message
      })
    },
    handleSaveSuccess (response) {
      this.setPage(response.data)
      this.isSaving = false
      this.displayToastNotification({
        type: 'success',
        icon: 'fa-check',
        title: 'Page Saved',
        info: `Page "${this.currentPage.title}" has been successfully saved.`
      })
    },
    saveAndPreview () {
      this.savePage()
      this.$router.push(`/${this.currentPage.menu.slug}/${this.currentPage.slug}`)
    },
    savePage () {
      for (let i = 0; i < this.blocks.length; i++) {
        this.blocks[i].order = i
      }
      for (let m = 0; m < this.modules.length; m++) {
        this.modules[m].order = m
      }
      this.isSaving = true
      this.errorBag.clear()
      if (this.id) {
        api.patch(`pages/${this.id}`, this.currentPage)
          .then(response => {
            this.handleSaveSuccess(response)
          })
          .catch(error => {
            this.handleSaveError(error)
          })
      } else {
        api.post(`pages`, this.currentPage)
          .then(response => {
            this.handleSaveSuccess(response)
            this.$router.back()
          })
          .catch(error => {
            this.handleSaveError(error)
          })
      }
    },
    deletePage () {
      this.showDeleteModal = false
      api.delete(`pages/${this.id}`)
        .then(response => {
          this.displayToastNotification({
            type: 'success',
            icon: 'fa-check',
            title: 'Page Deleted',
            info: `Page "${this.currentPage.title}" has been successfully deleted.`
          })
          this.$router.back()
        })
        .catch(error => {
          this.displayToastNotification({
            type: 'danger',
            icon: 'fa-exclamation',
            title: 'Unable to delete page',
            info: error.response.data.message
          })
        })
    },
    addBlock () {
      this.blocks.push({
        type: 'text',
        title: null,
        settings: { content: '' }
      })
    },
    removeBlock (index) {
      this.blocks.splice(index, 1)
    },
    blockContent (block) {
      if (!block.settings) {
        block.settings = { content: '' }
      }
      return block.settings.content
    },
    addModule (module) {
      this.modules.push(module)
      this.isPickingModule = false
    },
    removeModule (index) {
      this.modules.splice(index, 1)
    },
    onContentUpdate (module, html) {
      module.content = html
    },
    onTinyMceContentChange (event, editor) {
      let content = editor.getContent()
      let emptyListItem = '<li>&nbsp;</li>'
      if (content.includes('mce-toc') && content.includes(emptyListItem)) {
        content = content.replace(new RegExp(emptyListItem, 'g'), '')
        if (typeof content === 'string' && content.length !== 0) {
          editor.setContent(content)
        }
      }
    },
    move (from, to) {
      this.blocks.splice(to, 0, this.blocks.splice(from, 1)[0])
    },
    moveModule (from, to) {
      this.modules.splice(to, 0, this.modules.splice(from, 1)[0])
    },
    toggleSelectAllCountries (flag) {
      if (flag) {
        this.currentPage.assigned_countries = []
        for (var index in this.country_list) {
          this.currentPage.assigned_countries.push(this.country_list[index].id)
        }
      } else {
        this.currentPage.assigned_countries = []
      }
    }
  }
}
</script>
